import { reactive, markRaw } from 'vue';

class OverlayService {
  constructor() {
    this.state = reactive({
      overlays: [],
      baseZIndex: 9000
    });
    this.escapeListeners = new WeakMap(); // Better memory management
    this.zIndexCache = new Map(); // Cache z-index calculations
  }

  /**
   * Opens a new overlay (modal or sidebar)
   * @param {Object} config - Configuration object for the overlay
   * @param {Component} config.component - Vue component to render
   * @param {Object} config.props - Props to pass to the component
   * @param {Object} config.events - Event listeners for the component
   * @param {String} config.type - Type of overlay ('modal' | 'sidebar')
   * @param {String} config.position - Position for sidebar ('left' | 'right') or modal ('center' | 'top' | 'bottom')
   * @param {String} config.size - Size of the overlay ('sm' | 'md' | 'lg' | 'xl' | 'full')
   * @param {Boolean} config.backdrop - Whether to show backdrop
   * @param {Boolean} config.closeOnBackdrop - Whether clicking backdrop closes the overlay
   * @param {Boolean} config.closeOnEscape - Whether pressing escape closes the overlay
   * @param {Function} config.onClose - Callback when overlay is closed
   * @param {Function} config.onOpen - Callback when overlay is opened
   * @param {Function} config.beforeClose - Callback that can prevent closing by returning false
   * @returns {String} - Unique ID of the opened overlay
   */
  open(config) {
    const id = this.generateId();
    const overlay = {
      id,
      component: markRaw(config.component),
      props: config.props || {},
      events: config.events || {},
      type: config.type || 'modal',
      position: config.position || (config.type === 'sidebar' ? 'right' : 'center'),
      size: config.size || 'md',
      backdrop: config.backdrop !== false,
      closeOnBackdrop: config.closeOnBackdrop !== false,
      closeOnEscape: config.closeOnEscape !== false,
      onClose: config.onClose,
      onOpen: config.onOpen,
      beforeClose: config.beforeClose,
      zIndex: this.getZIndex(),
      visible: false
    };

    this.state.overlays.push(overlay);

    // Trigger opening animation
    setTimeout(() => {
      const overlayIndex = this.state.overlays.findIndex(o => o.id === id);
      if (overlayIndex !== -1) {
        this.state.overlays[overlayIndex].visible = true;
        if (overlay.onOpen) {
          overlay.onOpen();
        }
      }
    }, 10);

    // Setup escape key listener if needed
    if (overlay.closeOnEscape) {
      this.setupEscapeListener(id);
    }

    return id;
  }

  /**
   * Closes an overlay by ID
   * @param {String} id - ID of the overlay to close
   * @param {Any} data - Optional data to pass to onClose callback
   * @param {Boolean} force - Skip beforeClose validation (for programmatic closing)
   */
  close(id, data = null, force = false) {
    const overlayIndex = this.state.overlays.findIndex(o => o.id === id);
    if (overlayIndex === -1) return;

    const overlay = this.state.overlays[overlayIndex];
    
    // Check if beforeClose callback prevents closing (unless forced)
    if (!force && overlay.beforeClose && overlay.beforeClose() === false) {
      return false;
    }
    
    // Trigger closing animation
    overlay.visible = false;

    // Call onClose callback if provided
    if (overlay.onClose) {
      overlay.onClose(data);
    }

    // Remove overlay after animation completes
    setTimeout(() => {
      const index = this.state.overlays.findIndex(o => o.id === id);
      if (index !== -1) {
        this.state.overlays.splice(index, 1);
        this.clearZIndexCache(); // Clear cache when overlays change
      }
      this.removeEscapeListener(id);
    }, 300);
    
    return true;
  }

  /**
   * Closes all overlays
   * @param {Boolean} force - Skip beforeClose validation for all overlays
   */
  closeAll(force = false) {
    const ids = this.state.overlays.map(o => o.id);
    ids.forEach(id => this.close(id, null, force));
  }

  /**
   * Closes the topmost overlay
   * @param {Boolean} force - Skip beforeClose validation
   */
  closeTop(force = false) {
    if (this.state.overlays.length > 0) {
      const topOverlay = this.state.overlays[this.state.overlays.length - 1];
      this.close(topOverlay.id, null, force);
    }
  }

  /**
   * Force closes an overlay by ID (convenience method)
   * @param {String} id - ID of the overlay to close
   * @param {Any} data - Optional data to pass to onClose callback
   */
  forceClose(id, data = null) {
    return this.close(id, data, true);
  }

  /**
   * Updates props of an existing overlay
   * @param {String} id - ID of the overlay
   * @param {Object} props - New props to merge
   */
  updateProps(id, props) {
    const overlay = this.state.overlays.find(o => o.id === id);
    if (overlay) {
      overlay.props = { ...overlay.props, ...props };
    }
  }

  /**
   * Replaces the current overlay with a new one (for smooth transitions)
   * @param {Object} config - Configuration for the new overlay
   * @returns {String} - ID of the new overlay
   */
  replace(config) {
    // Close the current top overlay without animation delay
    if (this.state.overlays.length > 0) {
      const topOverlay = this.state.overlays[this.state.overlays.length - 1];
      const index = this.state.overlays.findIndex(o => o.id === topOverlay.id);
      if (index !== -1) {
        // Remove immediately without animation
        this.state.overlays.splice(index, 1);
        this.clearZIndexCache(); // Clear cache when overlays change
        this.removeEscapeListener(topOverlay.id);
        if (topOverlay.onClose) {
          topOverlay.onClose();
        }
      }
    }
    
    // Open new overlay immediately
    return this.open(config);
  }

  /**
   * Gets an overlay by ID
   * @param {String} id - ID of the overlay
   * @returns {Object|null} - The overlay object or null
   */
  getOverlay(id) {
    return this.state.overlays.find(o => o.id === id) || null;
  }

  /**
   * Gets all overlays
   * @returns {Array} - Array of all overlays
   */
  getAllOverlays() {
    return this.state.overlays;
  }

  /**
   * Checks if any overlay is open
   * @returns {Boolean}
   */
  hasOpenOverlays() {
    return this.state.overlays.length > 0;
  }

  /**
   * Gets optimized z-index for new overlay
   * @private
   */
  getZIndex() {
    const cacheKey = this.state.overlays.length;
    if (this.zIndexCache.has(cacheKey)) {
      return this.zIndexCache.get(cacheKey);
    }
    const zIndex = this.state.baseZIndex + (this.state.overlays.length * 10);
    this.zIndexCache.set(cacheKey, zIndex);
    return zIndex;
  }

  /**
   * Clears z-index cache when overlays change
   * @private
   */
  clearZIndexCache() {
    this.zIndexCache.clear();
  }

  /**
   * Generates a unique ID for an overlay
   * @private
   */
  generateId() {
    return `overlay-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Sets up escape key listener for an overlay
   * @private
   */
  setupEscapeListener(id) {
    const overlay = this.state.overlays.find(o => o.id === id);
    if (!overlay) return;

    const handler = (e) => {
      if (e.key === 'Escape') {
        const currentOverlay = this.state.overlays.find(o => o.id === id);
        if (currentOverlay && currentOverlay.closeOnEscape) {
          // Only close if this is the topmost overlay
          const topOverlay = this.state.overlays[this.state.overlays.length - 1];
          if (topOverlay && topOverlay.id === id) {
            this.close(id);
          }
        }
      }
    };

    this.escapeListeners.set(overlay, handler);
    document.addEventListener('keydown', handler);
  }

  /**
   * Removes escape key listener for an overlay
   * @private
   */
  removeEscapeListener(id) {
    const overlay = this.state.overlays.find(o => o.id === id);
    if (overlay && this.escapeListeners.has(overlay)) {
      const handler = this.escapeListeners.get(overlay);
      document.removeEventListener('keydown', handler);
      this.escapeListeners.delete(overlay);
    }
  }
}

// Create singleton instance
const overlayService = new OverlayService();

// Export singleton instance and composable
export default overlayService;

// Vue 3 Composable
export function useOverlay() {
  return {
    open: (config) => overlayService.open(config),
    close: (id, data, force) => overlayService.close(id, data, force),
    forceClose: (id, data) => overlayService.forceClose(id, data),
    closeAll: (force) => overlayService.closeAll(force),
    closeTop: (force) => overlayService.closeTop(force),
    replace: (config) => overlayService.replace(config),
    updateProps: (id, props) => overlayService.updateProps(id, props),
    getOverlay: (id) => overlayService.getOverlay(id),
    getAllOverlays: () => overlayService.getAllOverlays(),
    hasOpenOverlays: () => overlayService.hasOpenOverlays(),
    state: overlayService.state
  };
}